// react
import { useMemo } from 'react';
import styled, { css } from 'styled-components';

// component
import { ArticleCard } from '@/components/atoms/ArticleCard/ArticleCard';
import { LoadingTransition } from '@/components/atoms/LoadingTransition/LoadingTransition';
import { Title } from '@/components/atoms/Title/Title';

import { ArticleSummary } from '@/components/organisms/ArticleSummary/ArticleSummary';

import { CardContainer } from '@/components/templates/CardContainer/CardContainer';

// store
import { Article } from '@karehero/models';

// theme
import theme from '@/theme';

// hooks
import { useWindowSize } from '@/hooks';

type DummyArticle = {
  title: string;
};

type DummyArticleCategory = {
  title: string;
  articles: DummyArticle[];
};

/**
 * ArticleList is a page that displays all of the content that available
 * to the user.
 */
export const ArticleList = ({
  articles,
  isLoading,
  organizationName,
  isHeaderHidden = false,
  onArticleClick,
}: ArticleListProps) => {
  // hooks
  const [width] = useWindowSize();

  // memos
  const dummyArticles = useMemo(() => {
    let dummyArticles: DummyArticle[] = [];
    for (let i = 0; i < 32; i++) {
      dummyArticles.push({ title: `Article ${i}` });
    }
    return dummyArticles;
  }, []);

  const dummyLibrary = useMemo(() => {
    let dummyLibrary: DummyArticleCategory[] = [];
    for (let i = 0; i < 4; i++) {
      dummyLibrary.push({
        title: `Category ${i}`,
        articles: dummyArticles,
      });
    }
    return dummyLibrary;
  }, [dummyArticles]);

  const maxColumns = useMemo(() => {
    if (width < theme.breakpoint.mdValue) return 1;
    if (width < theme.breakpoint.lgValue) return 2;
    if (width < theme.breakpoint.xlValue) return 3;
    return 4;
  }, [width]);

  return (
    <StyledArticleList>
      {!isHeaderHidden && (
        <MainHeader>
          <Header>
            <Title isNoMargin>{organizationName} Resources</Title>
          </Header>
        </MainHeader>
      )}
      <LoadingTransition
        isLoading={isLoading}
        loading={
          <Content>
            <ArticleSummary library={dummyLibrary} isLoading />
          </Content>
        }
        content={
          <CardContainer maxColumns={maxColumns}>
            {articles && articles.length > 0 ? (
              articles?.map((article, i) => (
                <>
                  <ArticleCard
                    key={i}
                    article={article}
                    onClick={onArticleClick}
                    isLoading={isLoading}
                  />
                </>
              ))
            ) : (
              <div>No articles found</div>
            )}
          </CardContainer>
        }
      />
    </StyledArticleList>
  );
};

const StyledArticleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[36]};
  padding-bottom: ${(props) => props.theme.spacing[8]};
  padding-left: ${(props) => props.theme.spacing[4]};
  padding-right: ${(props) => props.theme.spacing[4]};
`;

const MainHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[36]};
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
`;

const Content = styled.div`
  display: flex;
  gap: ${(props) => props.theme.spacing[32]};
  flex-wrap: wrap;
`;

interface ArticleListProps {
  articles?: Article[];
  isLoading?: boolean;
  organizationName: string;
  isHeaderHidden?: boolean;
  onArticleClick?: (article?: Partial<Article>) => void;
}

ArticleList.displayName = 'ArticleList';

export default ArticleList;
